/**
 * Central, editable configuration for the Portal greeting system.
 * See utils/time.ts for the logic that reads this file.
 *
 * Every greeting string below must be inserted verbatim, including its
 * exact punctuation and capitalization — do not "clean up" or normalise
 * any of it. Where a string contains a curly apostrophe (’) or an ellipsis
 * character (…) rather than three periods, that is intentional.
 *
 * (NAME) is a literal placeholder token: at render time it is replaced with
 * the viewer's name exactly as the portal already derives it (unchanged by
 * this feature). Some Zeena easter-egg lines below do NOT contain (NAME) —
 * those are shown completely as written, with no substitution.
 */

// ---------------------------------------------------------------------
// FIXED-DATE STAFF GREETINGS
// Each entry fires for every year on the given month/day (in Asia/Dubai
// local calendar time), for the entire day, and overrides the normal
// time-of-day rotation. If more than one entry matches the same date,
// or an entry lists more than one template, one is chosen at random.
// Add or edit dates here — nothing else in the code needs to change.
// ---------------------------------------------------------------------
export interface FixedDateGreeting {
  id: string
  /** 1–12 */
  month: number
  /** 1–31 */
  day: number
  templates: string[]
}

export const FIXED_DATE_GREETINGS: FixedDateGreeting[] = [
  { id: 'christmas-eve', month: 12, day: 24, templates: ['’TWAS THE NIGHT BEFORE CHRISTMAS, (NAME)…'] },
  {
    id: 'christmas-day',
    month: 12,
    day: 25,
    templates: ['MERRY CHRISTMAS, (NAME)', 'IT’S CHRISTMAS, (NAME). LOG OFF.'],
  },
  { id: 'new-years-eve', month: 12, day: 31, templates: ['LAST ONE OF THE YEAR, (NAME)'] },
  { id: 'new-years-day', month: 1, day: 1, templates: ['HAPPY NEW YEAR, (NAME)'] },
  { id: 'uae-national-day', month: 12, day: 2, templates: ['HAPPY UAE NATIONAL DAY, (NAME)'] },
  { id: 'halloween', month: 10, day: 31, templates: ['HAPPY HALLOWEEN, (NAME)'] },
  { id: 'valentines-day', month: 2, day: 14, templates: ['HAPPY VALENTINE’S DAY, (NAME)'] },
]

// ---------------------------------------------------------------------
// TIME-GATED ADDITIONS TO A FIXED DATE
// These join the pool for a date above only once a certain time of day
// (Asia/Dubai) is reached, ON TOP OF that date's normal templates —
// they never replace them. Right now this is just Halloween evening,
// but more can be added the same way.
// `fromMinutesSince3am` uses the same "minutes since 3:00am" clock as
// the staff time-of-day windows in utils/time.ts (see the comment
// there for why) — 840 is 5:00pm, matching where "evening" begins.
// ---------------------------------------------------------------------
export interface EveningDateGreeting {
  id: string
  month: number
  day: number
  fromMinutesSince3am: number
  templates: string[]
}

export const EVENING_DATE_GREETINGS: EveningDateGreeting[] = [
  {
    id: 'halloween-evening',
    month: 10,
    day: 31,
    fromMinutesSince3am: 840,
    templates: ['SPOOKY SHIFT, (NAME)?', 'BOO! (NAME)'],
  },
]

// ---------------------------------------------------------------------
// EID DATES (movable — update every year)
// Eid does not fall on a fixed Gregorian date, so it is NOT in the fixed
// list above. Add each observed Eid date for the current/upcoming year(s)
// here as 'YYYY-MM-DD', in the Asia/Dubai calendar date. Multi-day Eid
// holidays should list every day. Remove old entries whenever convenient
// — nothing else in the code needs to change.
// Example: '2027-03-20',
// ---------------------------------------------------------------------
export const EID_DATES: string[] = [
  // Add this year's/next year's Eid al-Fitr and Eid al-Adha dates here.
]

export const EID_GREETING_TEMPLATE = 'EID MUBARAK, (NAME)'

// ---------------------------------------------------------------------
// STAFF BIRTHDAYS
// Highest-priority override of all — outranks time greetings, calendar
// greetings, and Zeena's easter eggs.
// Key: the staff member's Portal sign-in email, lowercase. Only month and
// day matter (1-12 / 1-31) — no year needed.
// ADD / EDIT STAFF BIRTHDAYS HERE:
// ---------------------------------------------------------------------
export const STAFF_BIRTHDAYS: Record<string, { month: number; day: number }> = {
  'zeena@portalproductions.me': { month: 8, day: 21 },
  'shilpa@portalproductions.me': { month: 10, day: 19 },
  'sreyangshi@portalproductions.me': { month: 1, day: 5 },
  'tom@portalproductions.me': { month: 7, day: 5 },
}

export const BIRTHDAY_GREETING_TEMPLATE = 'HAPPY BIRTHDAY, (NAME)!'

// ---------------------------------------------------------------------
// ZEENA — identity and easter-egg lines.
// Her real Portal staff sign-in email (lowercase) — confirmed against
// staff_users. The easter eggs below only ever fire for whoever is
// signed in with this exact email.
// ---------------------------------------------------------------------
export const ZEENA_EMAIL = 'zeena@portalproductions.me'

/**
 * These are shown completely as written — no (NAME) substitution, and
 * casing must be preserved exactly as given (they are deliberately NOT
 * all-caps like the other greetings).
 */
export const ZEENA_EASTER_EGGS: { id: string; text: string }[] = [
  { id: 'zeena-1', text: 'Remember Zeena, triples makes it safe. Triples is best.' },
  { id: 'zeena-2', text: 'Zeena, I feel like you’re just here for the zip line.' },
  { id: 'zeena-3', text: 'You think this is slicked back, Zeena? This is pushed back.' },
  { id: 'zeena-4', text: 'YOU SURE ABOUT THAT, ZEENA?' },
]

/** Roughly this fraction of Zeena's ordinary staff-greeting selections
    go off-script instead. Applies only when it is not her birthday and
    no calendar override is active today. */
export const ZEENA_EASTER_EGG_CHANCE = 0.1
